import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { EntitlementResponse } from '@api/types/entitlements';
import { categoryLabel } from '@components/Marketplace/marketplaceSkills';
import { useEntitlements } from '@hooks/useEntitlements';
import { ALL_TAB } from './AppsFooter.constants';
import type { FooterTab } from './footerTab';

export interface FooterSkills {
  readonly skills: readonly EntitlementResponse[];
  readonly tabs: readonly FooterTab[];
  readonly isLoading: boolean;
  readonly isError: boolean;
}

export const useFooterSkills = (enabled: boolean): FooterSkills => {
  const intl = useIntl();
  const { entitlements, isLoading, isError } = useEntitlements(enabled);

  const tabs = useMemo(() => {
    const categories = [...new Set(entitlements.map((skill) => skill.category))];
    return [
      {
        id: ALL_TAB,
        label: intl.formatMessage({ id: 'appsFooter.tab.all', defaultMessage: 'All' }),
        count: entitlements.length,
      },
      ...categories.map((category) => ({
        id: category,
        label: categoryLabel(intl, category),
        count: entitlements.filter((skill) => skill.category === category).length,
      })),
    ];
  }, [entitlements, intl]);

  return { skills: entitlements, tabs, isLoading, isError };
};

export const skillsInTab = (
  skills: readonly EntitlementResponse[],
  tab: string,
): readonly EntitlementResponse[] =>
  tab === ALL_TAB ? skills : skills.filter((skill) => skill.category === tab);
